import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCheck, ShieldCheck, Download, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtFechaHora } from "@/lib/fechas";

const ICONS = {
  inspeccion: ShieldCheck,
  descarga: Download,
  sistema: Info,
};

export default function NotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/v1/notificaciones", { params: { limit: 15 } });
      setItems(data.items);
      setUnread(data.unread);
    } catch (_e) { /* silencio */ }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const marcarTodas = async () => {
    await api.post("/api/v1/notificaciones/marcar-todas-leidas");
    load();
  };

  const abrir = async (n) => {
    if (!n.leida) {
      try { await api.post(`/api/v1/notificaciones/${n.id}/leer`); } catch (_e) { /* ignore */ }
    }
    load();
    if (n.deca_id) nav(`/decas/${n.deca_id}`);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative"
          data-testid="btn-notifications"
          aria-label={unread > 0 ? `Notificaciones, ${unread} sin leer` : "Notificaciones"}
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          {unread > 0 ? (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] leading-none px-1.5 py-0.5 rounded-full font-mono" data-testid="notif-badge">{unread}</span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notif-popover">
        <div className="flex items-center justify-between p-3 border-b border-slate-200">
          <div>
            <div className="font-semibold text-slate-900">Alertas</div>
            <div className="text-xs text-slate-500">{unread} sin leer</div>
          </div>
          <Button variant="ghost" size="sm" onClick={marcarTodas} disabled={unread === 0} data-testid="btn-mark-all">
            <CheckCheck className="h-4 w-4 mr-1"/> Marcar todas
          </Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">Aún no hay alertas.</div>
          ) : items.map((n) => {
            const Icon = ICONS[n.tipo] || Info;
            return (
              <button
                key={n.id}
                onClick={() => abrir(n)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors ${n.leida ? "" : "bg-blue-50/40"}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${n.tipo === "inspeccion" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-slate-100 text-slate-700 border border-slate-200"}`}>
                    <Icon className="h-4 w-4"/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{n.titulo}</div>
                    <div className="text-xs text-slate-600 line-clamp-2">{n.mensaje}</div>
                    <div className="text-[11px] text-slate-400 mt-1 font-mono">
                      {fmtFechaHora(n.created_at)}
                      {n.meta?.ip ? ` · ${n.meta.ip}` : ""}
                    </div>
                  </div>
                  {!n.leida ? <span className="h-2 w-2 rounded-full bg-blue-600 mt-1"/> : null}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
